import { UrlsTableErrorState } from "components/UrlsTableStates";
import { TableCountText } from "components/TableCountText";
import LabelledSelect from "components/LabelledSelect";
import { SelectOption } from "components/Select";
import { SearchInput } from "components/SearchInput";
import Pagination from "components/Pagination";
import Spinner from "components/Spinner";
import JobUrlsTable from "pages/crawler/components/jobs/JobUrlsTable";
import {
  REQUEST_STATUS_FILTER_OPTIONS,
  RequestStatusFilter,
  type JobUrl,
  type PaginationInfo,
} from "pages/crawler/components/jobs/types";
import { JOB_URLS_PAGE_SIZE } from "pages/crawler/constants/crawlerConstants";

interface JobUrlsTableSectionProps {
  urls: JobUrl[];
  pagination: PaginationInfo | null;
  isLoading: boolean;
  error: string | null;
  searchInput: string;
  setSearchInput: (value: string) => void;
  setPage: (page: number) => void;
  statusFilter: RequestStatusFilter;
  setStatusFilter: (value: RequestStatusFilter) => void;
  jobId?: string;
}

const LoadingSpinner = () => {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-12">
      <div className="flex items-center justify-center flex-col">
        <Spinner className="w-5 h-5 animate-spin m-3 text-neutral-500" />
        <span className="text-neutral-600">Loading URLs...</span>
      </div>
    </div>
  );
};

export const JobUrlsTableSection = ({
  urls,
  pagination,
  isLoading,
  error,
  searchInput,
  setSearchInput,
  setPage,
  statusFilter,
  setStatusFilter,
  jobId,
}: JobUrlsTableSectionProps) => {
  const currentPage = pagination?.page ?? 1;
  const total = pagination?.total ?? 0;
  const hasNextPage = pagination?.hasNextPage ?? false;
  const pageSize = pagination?.limit ?? JOB_URLS_PAGE_SIZE;

  return (
    <div className="mb-6">
      <div className="pt-4">
        <TableCountText total={total} page={currentPage} pageSize={pageSize} isLoading={isLoading} />
      </div>
      <div className="flex items-center justify-between py-4 flex-wrap gap-4">
        <div className="w-64">
          <SearchInput
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            placeholder="Search URLs"
            className="w-full"
          />
        </div>
        <div className="flex items-center gap-4 flex-wrap justify-end">
          <LabelledSelect
            label="Status"
            value={REQUEST_STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ?? REQUEST_STATUS_FILTER_OPTIONS[0]}
            options={REQUEST_STATUS_FILTER_OPTIONS}
            onChange={(option: SelectOption<RequestStatusFilter>) => setStatusFilter(option.value)}
            className="w-[216px]"
          />
          {total > 0 && (
            <div className="[&>div]:mt-0">
              <Pagination
                currentPage={currentPage}
                onPageChange={setPage}
                hasNextPage={hasNextPage}
              />
            </div>
          )}
        </div>
      </div>

      {renderTableContent()}
    </div>
  );

  function renderTableContent() {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (error) {
      return <UrlsTableErrorState error={error} />;
    }

    return (
      <>
        <JobUrlsTable
          urls={urls}
          hasActiveFilters={statusFilter !== "all" || searchInput.trim() !== ""}
        />
      </>
    );
  }
};

export default JobUrlsTableSection;
